import { useState } from 'react'

import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect'
import { throttle } from '@/utils/func'

const useInViewport = (
  element,
  {
    scrollableEl = typeof window !== 'undefined' && window,
    scrollThrottleInterval = 30,
    resizeThrottleInterval = 100,
    offsetTop = 0,
    offsetBottom = 0,
    offsetLeft = 0,
    offsetRight = 0,
    shouldUpdate = [],
    disable,
  } = {}
) => {
  const [isVisible, setIsVisible] = useState(undefined)
  let findElementTimeout

  const checkVisibility = (element) => {
    if (typeof element === 'string') {
      return undefined
    }

    if (!element || disable || !element.getBoundingClientRect()) {
      return false
    }

    const { top, right, bottom, left, width, height } =
      element.getBoundingClientRect()

    // check if all boundings equals 0
    // it means that component is stretched out all over the viewport
    // or its bugged
    if (top + right + bottom + left === 0) {
      return false
    }

    // Element bounding calculations (position aligned with element dimensions + offset)
    const topCheck = top + height + offsetTop
    const bottomCheck = bottom - height + offsetBottom
    const rightCheck = right - width + offsetRight
    const leftCheck = left + width + offsetLeft

    // Viewport dimensions
    const windowWidth =
      window.innerWidth || document.documentElement.clientWidth
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight

    // Check collisions with parts of viewport frame
    return (
      topCheck >= 0 &&
      leftCheck >= 0 &&
      bottomCheck <= windowHeight &&
      rightCheck <= windowWidth
    )
  }

  const findElementByQuery = () => {
    if (typeof window !== 'undefined') {
      const foundElement = window.document.querySelector(element)

      if (foundElement) {
        element = foundElement

        if (isVisible === undefined) {
          setIsVisible(checkVisibility(foundElement))
        }
      } else {
        findElementTimeout = setTimeout(findElementByQuery, 100)
      }
    }
  }

  useIsomorphicLayoutEffect(
    () => {
      if (typeof element === 'string') {
        findElementByQuery()
      }

      const checkIfVisible = () => setIsVisible(checkVisibility(element))

      const handleScroll = throttle(checkIfVisible, scrollThrottleInterval)
      const handleResize = throttle(checkIfVisible, resizeThrottleInterval)

      scrollableEl.addEventListener('scroll', handleScroll)
      window.addEventListener('resize', handleResize)

      return () => {
        scrollableEl.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', handleResize)
        clearTimeout(findElementTimeout)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [element, scrollableEl, ...shouldUpdate]
  )

  return isVisible
}

export default useInViewport
